import {
  Plus,
  Minus,
  ArrowUp,
  ArrowDown,
  Trash2,
  Copy,
  Clipboard,
  Undo,
  Redo,
} from 'lucide-react';
import clsx from 'clsx';

interface SpreadsheetToolbarProps {
  onAddRow: () => void;
  onAddColumn: () => void;
  onDeleteRow: () => void;
  onDeleteColumn: () => void;
  onSortAsc: () => void;
  onSortDesc: () => void;
  onCopy: () => void;
  onPaste: () => void;
  onUndo: () => void;
  onRedo: () => void;
  canUndo: boolean;
  canRedo: boolean;
  hasSelection: boolean;
  selectedColumn: number | null;
  selectedRow: number | null;
}

interface ToolbarButtonProps {
  onClick: () => void;
  disabled?: boolean;
  title: string;
  children: React.ReactNode;
}

function ToolbarButton({ onClick, disabled, title, children }: ToolbarButtonProps) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      title={title}
      className={clsx(
        'p-1.5 rounded transition-colors',
        disabled
          ? 'text-text-quaternary opacity-50 cursor-not-allowed'
          : 'text-text-secondary hover:bg-surface-hover hover:text-text-primary'
      )}
    >
      {children}
    </button>
  );
}

function ToolbarDivider() {
  return <div className="w-px h-5 bg-border-primary mx-1" />;
}

export function SpreadsheetToolbar({
  onAddRow,
  onAddColumn,
  onDeleteRow,
  onDeleteColumn,
  onSortAsc,
  onSortDesc,
  onCopy,
  onPaste,
  onUndo,
  onRedo,
  canUndo,
  canRedo,
  hasSelection,
  selectedColumn,
  selectedRow,
}: SpreadsheetToolbarProps) {
  return (
    <div className="flex items-center gap-0.5 px-2 py-0.5 border-b border-border-primary bg-surface-secondary/50 flex-wrap">
      {/* Undo/Redo */}
      <ToolbarButton onClick={onUndo} disabled={!canUndo} title="Annulla (Ctrl+Z)">
        <Undo className="w-3.5 h-3.5" />
      </ToolbarButton>
      <ToolbarButton onClick={onRedo} disabled={!canRedo} title="Ripeti (Ctrl+Y)">
        <Redo className="w-3.5 h-3.5" />
      </ToolbarButton>

      <ToolbarDivider />

      {/* Copy/Paste */}
      <ToolbarButton onClick={onCopy} disabled={!hasSelection} title="Copia (Ctrl+C)">
        <Copy className="w-3.5 h-3.5" />
      </ToolbarButton>
      <ToolbarButton onClick={onPaste} title="Incolla (Ctrl+V)">
        <Clipboard className="w-3.5 h-3.5" />
      </ToolbarButton>

      <ToolbarDivider />

      {/* Add rows/columns */}
      <div className="hidden sm:flex items-center gap-0.5 px-1.5 py-0.5 rounded bg-surface-tertiary/50">
        <ToolbarButton onClick={onAddRow} title="Aggiungi riga">
          <div className="flex items-center gap-1">
            <Plus className="w-3 h-3" />
            <span className="text-[11px]">Riga</span>
          </div>
        </ToolbarButton>
        <ToolbarButton onClick={onAddColumn} title="Aggiungi colonna">
          <div className="flex items-center gap-1">
            <Plus className="w-3 h-3" />
            <span className="text-[11px]">Col</span>
          </div>
        </ToolbarButton>
      </div>

      {/* Mobile add buttons */}
      <div className="flex sm:hidden items-center gap-0.5">
        <ToolbarButton onClick={onAddRow} title="Aggiungi riga">
          <Plus className="w-3.5 h-3.5" />
        </ToolbarButton>
      </div>

      {/* Delete */}
      <ToolbarButton
        onClick={onDeleteRow}
        disabled={selectedRow === null}
        title="Elimina riga selezionata"
      >
        <Minus className="w-3.5 h-3.5" />
      </ToolbarButton>
      <ToolbarButton
        onClick={onDeleteColumn}
        disabled={selectedColumn === null}
        title="Elimina colonna selezionata"
      >
        <Trash2 className="w-3.5 h-3.5" />
      </ToolbarButton>

      <ToolbarDivider />

      {/* Sort */}
      <div className="flex items-center gap-0.5">
        <ToolbarButton onClick={onSortAsc} disabled={selectedColumn === null} title="Ordina A-Z">
          <ArrowUp className="w-3.5 h-3.5" />
        </ToolbarButton>
        <ToolbarButton onClick={onSortDesc} disabled={selectedColumn === null} title="Ordina Z-A">
          <ArrowDown className="w-3.5 h-3.5" />
        </ToolbarButton>
      </div>
    </div>
  );
}
